import { useState } from 'react'
import { format } from 'date-fns'
import { UserPlus, Trash2, ShieldCheck, Users } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PageHeader } from '@/components/ui/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { TableSkeleton } from '@/components/ui/skeleton'
import { useConfirm } from '@/context/ConfirmProvider'
import { useUsers, useCreateUser, useDeleteUser, type AppUser } from '@/hooks/useUsers'
import { useT } from '@/i18n/I18nProvider'

function fmt(iso: string | null) {
  if (!iso) return '—'
  return format(new Date(iso), 'dd/MM/yyyy HH:mm')
}

// Configurações → Usuários: usuários do PRÓPRIO tenant (o owner gerencia os seus).
// A gestão cross-tenant (super-admin) fica no menu "Adm. usuários" (PlatformUsersPage).
export function UsersPage() {
  const { t } = useT()
  const { data, isLoading, error } = useUsers()
  const create = useCreateUser()
  const del = useDeleteUser()
  const confirm = useConfirm()

  const users = data?.users ?? []
  const canManage = data?.canManage ?? false
  // Árvore: o ADM (owner) no topo; os adicionais (members) abaixo como "filhos",
  // ordenados por quando entraram no tenant.
  const sorted = [...users].sort((a, b) => {
    const ra = a.role === 'owner' ? 0 : 1
    const rb = b.role === 'owner' ? 0 : 1
    if (ra !== rb) return ra - rb
    return (a.member_since ?? a.created_at).localeCompare(b.member_since ?? b.created_at)
  })

  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  async function onCreate(e: React.FormEvent) {
    e.preventDefault()
    try {
      // O servidor adiciona no próprio tenant do chamador (colaborador = member).
      await create.mutateAsync({ email: email.trim(), password })
      toast.success(t('mng.user.createdToast', { email: email.trim() }))
      setOpen(false)
      setEmail('')
      setPassword('')
    } catch (err) {
      toast.error(t('mng.user.errCreate'), { description: (err as Error).message })
    }
  }

  async function onDelete(u: AppUser) {
    const ok = await confirm({
      title: t('mng.user.removeTitle', { email: u.email ?? '' }),
      description: t('mng.user.removeDesc'),
      confirmText: t('common.remove'),
      tone: 'destructive',
    })
    if (!ok) return
    try {
      await del.mutateAsync(u.id)
      toast.success(t('mng.user.removed'))
    } catch (err) {
      toast.error(t('mng.user.errRemove'), { description: (err as Error).message })
    }
  }

  const roleLabel = (r: 'owner' | 'member' | null) =>
    r === 'owner' ? t('mng.role.owner') : r === 'member' ? t('mng.role.member') : '—'

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={t('settings.users')}
        actions={
          canManage ? (
            <Button onClick={() => setOpen(true)}>
              <UserPlus className="size-4" /> {t('mng.user.new')}
            </Button>
          ) : undefined
        }
      />

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <TableSkeleton rows={4} cols={4} />
          ) : error ? (
            <p className="text-destructive p-6 text-sm">{(error as Error).message}</p>
          ) : users.length === 0 ? (
            <EmptyState
              icon={Users}
              title={t('mng.user.emptyTitle')}
              description={t('mng.user.emptyDesc')}
              action={
                canManage ? (
                  <Button onClick={() => setOpen(true)}>
                    <UserPlus className="size-4" /> {t('mng.user.new')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('mng.user.colEmail')}</TableHead>
                    <TableHead>{t('mng.user.colRole')}</TableHead>
                    <TableHead>{t('mng.user.colCreated')}</TableHead>
                    <TableHead>{t('mng.user.colLastAccess')}</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((u) => {
                    const isChild = u.role !== 'owner'
                    return (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">
                        <span className={`inline-flex items-center gap-2 ${isChild ? 'pl-6' : ''}`}>
                          {isChild && <span className="text-muted-foreground select-none">↳</span>}
                          {u.email}
                          {u.is_self && (
                            <Badge variant="secondary" className="gap-1 text-[10px]">
                              <ShieldCheck className="size-3" /> {t('mng.user.you')}
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={u.role === 'owner' ? 'default' : 'outline'}
                          className="text-[10px]"
                        >
                          {roleLabel(u.role)}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">{fmt(u.created_at)}</TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">{fmt(u.last_sign_in_at)}</TableCell>
                      <TableCell>
                        {canManage && (
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={u.is_self || del.isPending}
                            onClick={() => onDelete(u)}
                            aria-label={t('common.remove')}
                            title={u.is_self ? t('mng.user.cantRemoveSelf') : t('common.remove')}
                          >
                            <Trash2 className="text-destructive size-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('mng.user.new')}</DialogTitle>
            <DialogDescription>{t('mng.user.dialogDesc')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={onCreate} className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="new-email">{t('mng.user.colEmail')}</Label>
              <Input id="new-email" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-pass">{t('mng.user.password')}</Label>
              <Input
                id="new-pass"
                type="text"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('mng.user.passwordPlaceholder')}
              />
              <p className="text-muted-foreground text-xs">{t('mng.user.passwordHint')}</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? t('mng.user.creating') : t('mng.user.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
